import React, { ReactText, useEffect, useMemo, useState } from "react";
import { Button, Modal, Pagination, Select, Table } from "antd";
import styled from "styled-components";
import { ExclamationCircleOutlined, PlusCircleFilled } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@redux/rootReducer";
import { SelectValue } from "antd/lib/select";
import { DiscountFrameTabItem, DiscountModule, TabListParams } from "@api/discountFrameApi";
import { ColumnsType } from "antd/lib/table";
import PhotoModal from "@component/UploadImageButton/PhotoModal";
import {
  fetchPlanList,
  updateSelectedPlanIds,
  toggleImportCsvPopup,
  updatePlanListParam,
  resetPlanListParam,
  batchUpdateDiscountTabList,
  batchDeletePlan,
} from "@redux/discountFrameSlice";
import { useParams } from "react-router-dom";
import ImportCSV from "./ImportCSV";
import TabSearchBar from "./TabSearchBar";
import AddSaleModal from "./AddSaleModal";

const Wrapper = styled.div`
  padding-top: 20px;
`;
const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 21px;
`;
const TopButtonContainer = styled.div`
  width: 70%;
  display: flex;
`;
const TopButton = styled(Button)`
  margin-right: 10px;
`;
const TablePageInfo = styled.div``;

const PageSelect = styled(Select)`
  margin: 0 10px;
`;
const TableContainer = styled.div`
  margin-bottom: 16px;
`;
const FrameWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const FrameDemoStyle = styled.img`
  width: 22px;
  height: 22px;
`;
const TableNoWrap = styled.div`
  white-space: nowrap;
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
`;
const DeleteButton = styled(Button)``;

const Link = styled.a`
  font-size: 13px;
  color: ${({ theme }) => theme.colorSuccess500};
`;

const HeaderButton = styled(Button)`
  margin-right: 10px;
`;

interface TableFrameItem extends DiscountFrameTabItem {
  key: number;
}

const pageSizeOptions = [20, 50, 100];

export default function PlanContent() {
  const dispatch = useDispatch();
  const { frameId } = useParams();
  const parseFrameId = Number(frameId);

  const isFetchingList = useSelector((state: RootState) => state.discountFrameSlice.isFetching);
  const planListParam = useSelector((state: RootState) => state.discountFrameSlice.planListParam);
  const selectedPlanIds = useSelector((state: RootState) => state.discountFrameSlice.selectedPlanIds);
  const planListResult = useSelector((state: RootState) => state.discountFrameSlice.planList);
  const showImportCsvPopup = useSelector((state: RootState) => state.discountFrameSlice.showImportCsvPopup);

  const { count, results: planList } = planListResult;
  const { offset, limit } = planListParam;

  const currentPage = useMemo(() => {
    return Math.floor(offset / limit) + 1;
  }, [limit, offset]);

  const [addModalState, setAddModalState] = useState(false);
  const [localFrameList, setLocalFrameList] = useState<TableFrameItem[]>([]);

  const [photoSource, setPhotoSource] = useState<string>("");

  const openImportCVS = () => {
    dispatch(toggleImportCsvPopup(true));
  };

  const fetchListWithFilter = (filter: TabListParams) => {
    dispatch(updatePlanListParam(filter));
    dispatch(fetchPlanList(parseFrameId));
  };

  const handleOnPageSizeChange = (value: SelectValue) => {
    const filter = {
      ...planListParam,
      limit: value as number,
    };
    fetchListWithFilter(filter);
  };
  const handleOnPageChange = (page: number) => {
    const filter = {
      ...planListParam,
      offset: (page - 1) * limit,
    };

    fetchListWithFilter(filter);
  };

  const handleOnPhotoClick = (url: string) => {
    setPhotoSource(url);
  };
  const closePhotoModal = () => {
    setPhotoSource("");
  };

  const handleOnCheck = (values: ReactText[]) => {
    dispatch(updateSelectedPlanIds(values as number[]));
  };

  const handleOnBatchDelete = () => {
    Modal.confirm({
      title: "你確定要刪除這些資料?",
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: "是",
      cancelText: "否",
      onOk: () => dispatch(batchDeletePlan(parseFrameId)),
    });
  };

  const handleAddPlan = (planIds: number[]) => {
    const params = {
      objectIds: planIds,
      module: DiscountModule.PLAN,
    };

    dispatch(batchUpdateDiscountTabList(params));
  };

  const tableColumns: ColumnsType<TableFrameItem> = [
    {
      title: <TableNoWrap>方案ID</TableNoWrap>,
      key: "objectId",
      dataIndex: "objectId",
      align: "center",
      width: 100,
      render: (value: DiscountFrameTabItem["objectId"], record: TableFrameItem) => (
        <Link href={`/salespage/edit/${record.salesPageId}`} target="_blank">
          {value}
        </Link>
      ),
    },
    {
      title: <TableNoWrap>方案名稱</TableNoWrap>,
      align: "left",
      key: "planName",
      dataIndex: "planName",
    },
    {
      title: <TableNoWrap>原始圖片</TableNoWrap>,
      key: "originalImagePath",
      dataIndex: "originalImagePath",
      width: 150,
      align: "center",
      render: (value: DiscountFrameTabItem["originalImagePath"]) => {
        return (
          <FrameWrapper>
            <FrameDemoStyle
              src={value}
              alt=""
              onClick={() => {
                handleOnPhotoClick(value);
              }}
            />
          </FrameWrapper>
        );
      },
    },
    {
      title: <TableNoWrap>新圖片</TableNoWrap>,
      key: "compositeImagePath",
      dataIndex: "compositeImagePath",
      width: 150,
      align: "center",
      render: (value: DiscountFrameTabItem["compositeImagePath"]) => {
        return (
          <FrameWrapper>
            <FrameDemoStyle
              src={value}
              alt=""
              onClick={() => {
                handleOnPhotoClick(value);
              }}
            />
          </FrameWrapper>
        );
      },
    },
  ];
  useEffect(() => {
    dispatch(fetchPlanList(parseFrameId));
    return () => {
      dispatch(resetPlanListParam());
    };
  }, [dispatch, parseFrameId]);

  useEffect(() => {
    const tableFrameList = planList.map((plan) => ({
      ...plan,
      key: plan.id,
    }));
    setLocalFrameList(tableFrameList);
  }, [planList]);

  return (
    <Wrapper>
      <Header>
        <TopButtonContainer>
          <TopButton type="primary" icon={<PlusCircleFilled />} onClick={() => setAddModalState(true)}>
            新增方案
          </TopButton>
          <HeaderButton onClick={openImportCVS}>匯入</HeaderButton>
          <TabSearchBar
            module={DiscountModule.PLAN}
            onSubmit={(filter) => fetchListWithFilter(filter)}
            onReset={() => {
              dispatch(resetPlanListParam());
              dispatch(fetchPlanList(parseFrameId));
            }}
          />
        </TopButtonContainer>
        <TablePageInfo>
          總共
          {count}
          筆, 每頁顯示
          <PageSelect value={limit} onChange={(value) => handleOnPageSizeChange(value as SelectValue)}>
            {pageSizeOptions.map((option) => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </PageSelect>
          筆
        </TablePageInfo>
      </Header>
      <TableContainer>
        <Table
          dataSource={localFrameList}
          columns={tableColumns}
          scroll={{ x: "max-content", y: 400 }}
          rowSelection={{
            onChange: handleOnCheck,
            selectedRowKeys: selectedPlanIds,
          }}
          tableLayout="auto"
          pagination={false}
          loading={isFetchingList}
        />
      </TableContainer>
      {photoSource && <PhotoModal source={photoSource} onClose={closePhotoModal} />}
      <Footer>
        <DeleteButton onClick={handleOnBatchDelete} disabled={selectedPlanIds.length === 0}>
          刪除
        </DeleteButton>
        <Pagination
          current={currentPage}
          total={count}
          onChange={handleOnPageChange}
          showSizeChanger={false}
          pageSize={limit}
        />
      </Footer>
      {showImportCsvPopup && (
        <ImportCSV
          module={DiscountModule.PLAN}
          templatePath="admin/template/csv/sales_plan_id_frame_import_sample.xlsx"
        />
      )}
      {addModalState && (
        <AddSaleModal submit={handleAddPlan} close={() => setAddModalState(false)} module={DiscountModule.PLAN} />
      )}
    </Wrapper>
  );
}
